import React, { useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Dialog from '@material-ui/core/Dialog';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import { colors } from '../../../../../constants/colors';
import { smallerThanOrEqualTo } from '../../../../../core/util/styledComponents';
import ConnectedBookModule from '../ConnectedBookModule';
import { LocationContext } from '../../../ContextProviders';
import dataTestIds from '~/test/test-ids';
import { moduleRootBoxName } from '~/components/ClinicDetailPageFlexible/components/BookModule/components/shared/ModuleRoot';
import { NewBookingState } from '~/reducers/newBooking';
import { ModuleVariant } from '~/components/ClinicDetailPageFlexible/components/BookModule';
import { AccountSummary } from '~/reducers/account';
import { analyticsTrackEvent } from '~/core/analytics';
import { CDP_BOOK_MODAL } from '~/core/analytics/events';

export const BOOKING_TIME_SELECTOR_MODAL = 'booking time selector modal';

const SmoothDialog = styled(Dialog).withConfig({ componentId: 'sc-m7lbdi-0' })`
  -webkit-font-smoothing: antialiased;
  -webkit-overflow-scrolling: touch;
`;

const Container = styled.div.withConfig({ componentId: 'sc-m7lbdi-1' })`
  background-color: white;
  display: block;
  margin-bottom: 16px;

  .${moduleRootBoxName} {
    box-shadow: none !important;
  }
`;

const MessageContainer = styled.div.withConfig({ componentId: 'sc-m7lbdi-2' })`
  width: 90%;
  margin: 0 auto;
`;

const Close = styled.div.withConfig({ componentId: 'sc-m7lbdi-3' })`
  width: 25px;
  height: 25px;
  position: absolute;
  right: 18px;
  top: 21px;
  cursor: pointer;
  margin: 0 0 -10px auto;
  ${smallerThanOrEqualTo.md`
    margin 0 0 0 auto;
  `}

  &:before {
    content: '';
    left: 50%;
    height: 100%;
    width: 1px;
    position: absolute;
    transform: rotate(45deg);
    background-color: ${colors.squid};
  }

  &:after {
    content: '';
    left: 50%;
    height: 100%;
    width: 1px;
    position: absolute;
    transform: rotate(-45deg);
    background-color: ${colors.squid};
  }
`;

type Props = {
  location?: any;
  handleRequestAppointmentClick: (
    appointmentTime: number | null | undefined,
    symptoms: string,
    location: LocationInterface,
    newBooking: NewBookingState
  ) => void;
  fullScreen?: boolean;
  onClose?: (...args: any[]) => any;
  open?: boolean;
  setBookingProfile?: (accountSummary: AccountSummary, userProfileId: string) => void;
  requestAppointmentData?: any;
  nearbyTelemedLocation?: any;
  newBooking?: any;
  setCurrentlySelectedLocation: (value: LocationInterface) => void;
  // below props are from old booking flow
  accountSummary?: any;
  login?: any;
  reasonForVisit?: string;
  suggestions?: any[];
};

const BookModal = (props: Props) => {
  const {
    handleRequestAppointmentClick,
    requestAppointmentData,
    setBookingProfile,
    fullScreen,
    onClose,
    open = false,
    newBooking,
    setCurrentlySelectedLocation,
    // below props are from old booking flow
    login,
    accountSummary,
    suggestions,
    reasonForVisit,
  } = props;

  const { location } = useContext(LocationContext);

  const appointmentData = useRef(requestAppointmentData);
  useEffect(() => {
    if (requestAppointmentData && requestAppointmentData !== appointmentData.current) {
      handleRequestAppointmentClick(
        requestAppointmentData?.appointmentTime,
        '',
        location,
        newBooking
      );
      appointmentData.current = requestAppointmentData;
    }
  }, [handleRequestAppointmentClick, location, newBooking, requestAppointmentData]);

  useEffect(() => {
    analyticsTrackEvent(CDP_BOOK_MODAL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SmoothDialog
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xs"
      onClose={onClose}
      open={open}
      scroll="body"
    >
      <Container>
        <Close data-testid={dataTestIds.cdp.bookingModalClose} onClick={onClose} />

        <ConnectedBookModule
          accountSummary={accountSummary}
          autoFocus
          handleRequestAppointmentClick={handleRequestAppointmentClick}
          login={login}
          newBooking={newBooking}
          reasonForVisit={reasonForVisit}
          setBookingProfile={setBookingProfile}
          setCurrentlySelectedLocation={setCurrentlySelectedLocation}
          suggestions={suggestions}
          variant={ModuleVariant.MODAL}
        />
      </Container>
    </SmoothDialog>
  );
};

export default withMobileDialog({ breakpoint: 'xs' })(BookModal);
